import React from 'react';
import { Sparkles, Copy } from 'lucide-react';
import ShareArrowIcon from './ShareArrowIcon';
import { ModalCloseButton } from './ModalCloseButton';

interface InterpretationResultModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCopy: () => Promise<boolean> | boolean;
  onShare: () => void;
  onRequestNew: () => void;
  canRequestAnother: boolean;
  priceLabel: string;
  interpretation: string;
  title: string;
}

export const InterpretationResultModal: React.FC<InterpretationResultModalProps> = ({
  isOpen,
  onClose,
  onCopy,
  onShare,
  onRequestNew,
  canRequestAnother,
  priceLabel,
  interpretation,
  title,
}) => {
  if (!isOpen) return null;

  const [copyStatus, setCopyStatus] = React.useState<'idle' | 'copied' | 'failed'>('idle');
  const resetTimerRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    if (copyStatus === 'idle') return;

    resetTimerRef.current = window.setTimeout(() => {
      setCopyStatus('idle');
    }, 1400);

    return () => {
      if (resetTimerRef.current) {
        window.clearTimeout(resetTimerRef.current);
        resetTimerRef.current = null;
      }
    };
  }, [copyStatus]);

  const handleCopy = React.useCallback(async () => {
    try {
      const result = await onCopy();
      setCopyStatus(result ? 'copied' : 'failed');
    } catch (error) {
      console.error('Copy callback failed:', error);
      setCopyStatus('failed');
    }
  }, [onCopy]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      void handleCopy();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[80] p-4">
      <div className="absolute inset-0" onClick={onClose} aria-hidden />

      <div className="relative bg-gradient-to-b from-slate-900 to-purple-950 rounded-2xl max-w-lg w-full mx-4 shadow-2xl border border-purple-500/20 overflow-hidden min-h-[520px]">
        <ModalCloseButton onClose={onClose} className="text-white/80" />

        <div className="px-6 py-8 space-y-6 h-full">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-200" />
              <h2 className="crypto-heading text-white text-xl">{title}</h2>
            </div>
            <p className="text-white/65 text-sm leading-relaxed">
              Share or save this glimpse from Stradamus. Keep it close to revisit later.
            </p>
          </div>

          <div className="relative bg-black/20 rounded-xl border border-white/10 flex-1 max-h-64">
            <div
              role="button"
              tabIndex={0}
              onClick={() => void handleCopy()}
              onKeyDown={handleKeyDown}
              className="group relative block w-full h-full cursor-copy focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-400/70 rounded-xl"
            >
              <div className="absolute top-3 right-3 text-white/40 group-hover:text-white/60 transition-colors pointer-events-none">
                <Copy className="w-4 h-4" />
              </div>
              <div className="py-4 px-4 pr-10 text-sm leading-relaxed text-white/85 whitespace-pre-wrap overflow-y-auto max-h-64">
                {interpretation}
              </div>
              {copyStatus === 'copied' && (
                <div className="absolute bottom-3 right-3 bg-purple-500/80 text-white text-xs font-medium px-3 py-1 rounded-full">
                  copied
                </div>
              )}
              {copyStatus === 'failed' && (
                <div className="absolute bottom-3 right-3 bg-red-500/80 text-white text-xs font-medium px-3 py-1 rounded-full">
                  copy failed
                </div>
              )}
            </div>
          </div>

          <div className="space-y-3">
            <button
              onClick={onShare}
              className="w-full flex items-center justify-center space-x-2 py-3 rounded-full bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600 transition-colors text-white text-sm font-semibold"
            >
              <ShareArrowIcon className="w-4 h-4" />
              <span>Share</span>
            </button>

            <button
              onClick={onRequestNew}
              disabled={!canRequestAnother}
              className={`w-full flex items-center justify-center gap-2 py-3 px-5 rounded-full border border-purple-500/40 text-sm font-semibold transition-colors ${
                canRequestAnother
                  ? 'bg-purple-950/40 text-purple-200 hover:bg-purple-900/60'
                  : 'bg-purple-950/20 text-purple-400/60 cursor-not-allowed'
              }`}
            >
              <Sparkles className="w-4 h-4" />
              <span>
                {canRequestAnother ? 'Request another insight' : 'Daily limit reached'}
              </span>
            </button>
            {canRequestAnother && priceLabel && (
              <p className="text-center text-xs text-purple-200/70">
                Price: {priceLabel}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
